<template>
  <div class="import-program-history">
    <h1>Import percorsi storici</h1>
    <p class="muted">
      Carica un file CSV con le colonne <strong>email</strong>, <strong>programmi totali</strong>,
      <strong>programmi da importare (attivi)</strong>, <strong>data di scadenza</strong> e, se disponibili,
      le nuove misure/foto di questionario iniziale.
    </p>

    <div class="upload-box">
      <label class="upload-label">
        <input
          ref="fileInput"
          type="file"
          accept=".csv,text/csv"
          @change="onFileChange"
        />
        <span v-if="!fileName">Seleziona CSV…</span>
        <span v-else>File selezionato: {{ fileName }}</span>
      </label>
      <button
        v-if="fileName"
        class="btn outline staff-button-cancel"
        type="button"
        @click="resetSelection"
      >
        Rimuovi file
      </button>
    </div>

    <div v-if="parseErrors.length" class="errors-box">
      <h3>Errori di parsing</h3>
      <ul>
        <li v-for="(err, idx) in parseErrors" :key="idx">{{ err }}</li>
      </ul>
    </div>

    <div v-if="rowWarnings.length" class="warnings-box">
      <h3>Righe ignorate</h3>
      <p class="muted">Queste righe non verranno importate.</p>
      <ul>
        <li v-for="(warn, idx) in rowWarnings" :key="`warn-${idx}`">{{ warn }}</li>
      </ul>
    </div>

    <div v-if="rows.length && !parseErrors.length" class="preview-card">
      <div class="preview-head">
        <div>
          <h3>Anteprima righe</h3>
          <p class="muted">
            Totale righe valide: {{ rows.length }}
            <span v-if="rows.length">· Importate: {{ processedCount }} / {{ rows.length }}</span>
          </p>
        </div>
        <button
          class="btn primary staff-button-positive"
          type="button"
          :disabled="!hasPendingRows || importingChunk"
          @click="importNextChunk"
        >
          {{ importingChunk ? 'Import in corso…' : chunkButtonLabel }}
        </button>
      </div>

      <table class="preview-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Email</th>
            <th>Percorso</th>
            <th>Programmi totali</th>
            <th>Programmi storici</th>
            <th>Programmi attivi</th>
            <th>Scadenza</th>
            <th>Misure iniziali</th>
            <th>Foto iniziali</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in previewRows" :key="row._id">
            <td>{{ index + 1 }}</td>
            <td>{{ row.email }}</td>
            <td>
              <div>{{ row.productName || row.programTitle || '—' }}</div>
              <small v-if="row.orderLabel || row.orderRef" class="muted">
                <template v-if="row.orderLabel">{{ row.orderLabel }}</template>
                <template v-else>Ordine #{{ row.orderRef }}</template>
              </small>
            </td>
            <td>{{ row.programsTotal }}</td>
            <td>{{ row.programsCompleted }}</td>
            <td>{{ row.programsActive }}</td>
            <td>{{ row.expireAtDisplay }}</td>
            <td>{{ measurementSummary(row) || '—' }}</td>
            <td>{{ photoSummary(row) || '—' }}</td>
          </tr>
        </tbody>
      </table>
      <p v-if="rows.length > previewRows.length" class="muted">
        Mostrate {{ previewRows.length }} righe (su {{ rows.length }}).
      </p>
    </div>

    <div v-if="batches.length" class="result-card">
      <div class="result-head">
        <div>
          <h3>Riepilogo import</h3>
          <p class="muted">
            Blocchi completati: {{ batches.length }} · Righe elaborate: {{ flattenedResults.length }}
          </p>
        </div>
      </div>

      <ul class="summary-list">
        <li>✅ Successi: {{ aggregatedSummary.success }}</li>
        <li>⚠️ Saltati: {{ aggregatedSummary.skipped }}</li>
        <li>❌ Errori: {{ aggregatedSummary.errors }}</li>
      </ul>

      <table class="result-table">
        <thead>
          <tr>
            <th>Batch</th>
            <th>Email</th>
            <th>Esito</th>
            <th>Dettaglio</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in flattenedResults" :key="`${row.batchId}-${row.email}`">
            <td>
              <div class="mono">#{{ row.batchIndex }}</div>
              <small class="muted"><code>{{ row.batchId }}</code></small>
              <span v-if="row.batchReverted" class="batch-tag">Revertito</span>
            </td>
            <td>{{ row.email }}</td>
            <td>
              <span
                class="status-chip"
                :class="statusClass(row.status)"
              >
                {{ row.status }}
              </span>
            </td>
            <td>
              <template v-if="row.status === 'success'">
                Creati {{ row.createdPercorsi?.length || 0 }} percorsi.
              </template>
              <template v-else-if="row.status === 'skipped'">
                {{ row.reason || 'Saltato' }}
              </template>
              <template v-else>
                {{ row.error || 'Errore sconosciuto' }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="batch-list">
        <div
          v-for="(batch, index) in batches"
          :key="batch.batchId"
          class="batch-card"
        >
          <div class="result-head">
            <div>
              <h4>Batch #{{ index + 1 }}</h4>
              <p class="muted">
                ID: <code>{{ batch.batchId }}</code>
                <span v-if="batch.reverted" class="batch-tag">Revertito</span>
              </p>
            </div>
            <button
              type="button"
              class="btn danger staff-button-danger"
              @click="revertBatch(batch)"
              :disabled="batch.reverted || getRevertState(batch.batchId).loading"
            >
              {{ getRevertState(batch.batchId).loading ? 'Eliminazione…' : 'Elimina batch' }}
            </button>
          </div>
          <ul class="summary-list">
            <li>✅ {{ batch.summary.success }}</li>
            <li>⚠️ {{ batch.summary.skipped }}</li>
            <li>❌ {{ batch.summary.errors }}</li>
          </ul>
          <p v-if="getRevertState(batch.batchId).success" class="success-message">
            {{ getRevertState(batch.batchId).success }}
          </p>
          <p v-else-if="getRevertState(batch.batchId).error" class="error-message">
            {{ getRevertState(batch.batchId).error }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { getFunctions, httpsCallable } from 'firebase/functions';

const fileInput = ref(null);
const fileName = ref('');
const rows = ref([]);
const parseErrors = ref([]);
const rowWarnings = ref([]);
const importingChunk = ref(false);
const processedCount = ref(0);
const batches = ref([]);
const revertStates = ref({});

const PREVIEW_LIMIT = 25;
const CHUNK_SIZE = 25;

const MEASUREMENT_FIELDS = [
  { key: 'peso', label: 'Peso (kg)', headerKeys: ['pesoinkg', 'peso'] },
  { key: 'altezza', label: 'Altezza (cm)', headerKeys: ['altezzaincm', 'altezza'] },
  { key: 'fianchi', label: 'Fianchi (cm)', headerKeys: ['fianchiincm', 'circonferenzafianchi', 'fianchi'] },
  { key: 'coscia', label: 'Coscia (cm)', headerKeys: ['cosciaincm', 'coscia'] },
  { key: 'addome', label: 'Addome (cm)', headerKeys: ['addomeincm', 'addome', 'vitaincm'] },
  { key: 'torace', label: 'Torace (cm)', headerKeys: ['toraceincm', 'torace'] },
  { key: 'braccio', label: 'Braccio (cm)', headerKeys: ['braccioincm', 'braccio'] },
  { key: 'massa_magra', label: 'Massa magra (%)', headerKeys: ['massamagra'] },
  { key: 'massa_grassa', label: 'Massa grassa (%)', headerKeys: ['massagrassa'] },
  { key: 'acqua_corporea', label: 'Acqua corporea (%)', headerKeys: ['acquacorporea'] }
];

const PHOTO_FIELDS = [
  { key: 'foto_frontale', label: 'Foto frontale', headerKeys: ['fotofrontale', 'fotoiniziale', 'fotofront'] },
  { key: 'foto_posteriore', label: 'Foto posteriore', headerKeys: ['fotoposteriore', 'fotoretro', 'fotoback'] },
  { key: 'foto_lato_sinistro', label: 'Foto lato sinistro', headerKeys: ['fotolatosinistro', 'fotolateralesinistro', 'fotolatosx'] },
  { key: 'foto_lato_destro', label: 'Foto lato destro', headerKeys: ['fotolatodestro', 'fotolateralediestro', 'fotolatodx'] }
];

const ORDER_SPLIT_REGEX = /\s*[-–]\s*/;
const looksLikeDateToken = (value) => {
  if (!value) return false;
  const normalized = String(value).trim();
  if (!normalized) return false;
  return /^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/.test(normalized);
};

const parseOrderInfo = (raw) => {
  const label = String(raw || '').trim();
  if (!label) {
    return {
      label: '',
      orderRef: '',
      productName: ''
    };
  }

  const refMatch = /#\s*([0-9]+)/i.exec(label);
  const orderRef = refMatch ? refMatch[1] : '';

  const parts = label
    .split(ORDER_SPLIT_REGEX)
    .map((part) => part.trim())
    .filter(Boolean);

  let working = [...parts];
  if (working.length && /ordine/i.test(working[0])) {
    working = working.slice(1);
  }
  if (working.length && looksLikeDateToken(working[working.length - 1])) {
    working = working.slice(0, working.length - 1);
  }

  const productName = working.join(' - ').trim() || label;

  return {
    label,
    orderRef,
    productName
  };
};

const measurementLabelMap = MEASUREMENT_FIELDS.reduce((acc, field) => {
  acc[field.key] = field.label;
  return acc;
}, {});

const photoLabelMap = PHOTO_FIELDS.reduce((acc, field) => {
  acc[field.key] = field.label;
  return acc;
}, {});

const sanitizeNumericInput = (value) => {
  if (value == null) return null;
  const asString = String(value).trim();
  if (!asString) return null;
  const normalized = asString
    .replace(/\s+/g, '')
    .replace(/,/g, '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseMeasurementValue = (raw) => {
  if (!raw) return null;
  const numeric = sanitizeNumericInput(raw);
  if (Number.isFinite(numeric)) return numeric;
  const trimmed = String(raw).trim();
  return trimmed.length ? trimmed : null;
};

const summarizeLabels = (labels = [], limit = 3) => {
  if (!labels.length) return '';
  if (labels.length <= limit) return labels.join(', ');
  const visible = labels.slice(0, limit).join(', ');
  return `${visible} +${labels.length - limit}`;
};

const extractMeasurements = (cells, headerMap) => {
  const out = {};
  if (!headerMap?.measurements) return out;
  MEASUREMENT_FIELDS.forEach((field) => {
    const idx = headerMap.measurements[field.key];
    if (typeof idx !== 'number' || idx < 0) return;
    const raw = (cells[idx] || '').trim();
    if (!raw) return;
    const value = parseMeasurementValue(raw);
    if (value === null || value === undefined || value === '') return;
    out[field.key] = value;
  });
  return out;
};

const extractPhotos = (cells, headerMap) => {
  const out = {};
  if (!headerMap?.photos) return out;
  PHOTO_FIELDS.forEach((field) => {
    const idx = headerMap.photos[field.key];
    if (typeof idx !== 'number' || idx < 0) return;
    const raw = (cells[idx] || '').trim();
    if (!raw) return;
    out[field.key] = raw;
  });
  return out;
};

const measurementSummary = (row) => {
  const labels = Object.keys(row?.measurements || {}).map((key) => measurementLabelMap[key] || key);
  return summarizeLabels(labels);
};

const photoSummary = (row) => {
  const labels = Object.entries(row?.photos || {})
    .filter(([, value]) => !!value)
    .map(([key]) => photoLabelMap[key] || key);
  return summarizeLabels(labels, 4);
};

const inferFileNameFromUrl = (url) => {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    const pathname = parsed.pathname || '';
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length) return segments.pop() || '';
  } catch (err) {
    // ignore malformed URLs
  }
  return '';
};

const guessContentTypeFromUrl = (url) => {
  const lower = String(url || '').toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.match(/\.(jpe?g)$/)) return 'image/jpeg';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  return '';
};

const normalizePhotoValue = (value, fallbackName = 'foto') => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  const asString = String(value || '').trim();
  if (!asString) return null;
  const name = inferFileNameFromUrl(asString) || fallbackName;
  const contentType = guessContentTypeFromUrl(asString);
  return {
    url: asString,
    path: asString,
    name,
    size: null,
    contentType
  };
};

const normalizePhotosForPayload = (photos = {}) => {
  if (!photos || typeof photos !== 'object') return {};
  const out = {};
  PHOTO_FIELDS.forEach((field) => {
    const entry = normalizePhotoValue(photos[field.key], field.label || field.key);
    if (entry) {
      out[field.key] = entry;
    }
  });
  return out;
};

const previewRows = computed(() => rows.value.slice(0, PREVIEW_LIMIT));
const totalRows = computed(() => rows.value.length);
const remainingRows = computed(() => Math.max(0, totalRows.value - processedCount.value));
const hasPendingRows = computed(() => remainingRows.value > 0);
const nextChunkRange = computed(() => {
  if (!hasPendingRows.value) return null;
  const start = processedCount.value + 1;
  const end = Math.min(processedCount.value + CHUNK_SIZE, totalRows.value);
  return { start, end };
});
const chunkButtonLabel = computed(() => {
  if (!totalRows.value) return 'Importa';
  if (!hasPendingRows.value) return 'Import completato';
  if (!nextChunkRange.value) return 'Importa';
  const { start, end } = nextChunkRange.value;
  return start === end ? `Importa riga ${start}` : `Importa righe ${start}-${end}`;
});
const flattenedResults = computed(() =>
  batches.value.flatMap((batch, index) =>
    (batch.results || []).map((row) => ({
      ...row,
      batchId: batch.batchId,
      batchIndex: index + 1,
      batchReverted: !!batch.reverted
    }))
  )
);
const aggregatedSummary = computed(() =>
  flattenedResults.value.reduce(
    (acc, row) => {
      if (row.status === 'success') acc.success += 1;
      else if (row.status === 'skipped') acc.skipped += 1;
      else acc.errors += 1;
      return acc;
    },
    { success: 0, skipped: 0, errors: 0 }
  )
);
const statusClass = (status) => {
  if (status === 'success') return 'status-chip--success';
  if (status === 'skipped') return 'status-chip--warning';
  return 'status-chip--error';
};
const getRevertState = (batchId) => revertStates.value[batchId] || {};

const resetSelection = () => {
  rows.value = [];
  parseErrors.value = [];
  rowWarnings.value = [];
  fileName.value = '';
  processedCount.value = 0;
  batches.value = [];
  revertStates.value = {};
  importingChunk.value = false;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const onFileChange = (event) => {
  const file = event.target.files?.[0];
  if (!file) {
    resetSelection();
    return;
  }
  fileName.value = file.name;
  parseErrors.value = [];
  rowWarnings.value = [];
  rows.value = [];
  processedCount.value = 0;
  batches.value = [];
  revertStates.value = {};

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const text = e.target?.result;
      if (typeof text !== 'string') {
        parseErrors.value.push('Formato file non valido.');
        return;
      }
      const parsed = parseCsv(text);
      rows.value = parsed.rows;
      rowWarnings.value = parsed.warnings;
      if (!rows.value.length) {
        parseErrors.value.push('Nessuna riga valida trovata nel CSV.');
      }
    } catch (err) {
      parseErrors.value.push(`Errore nella lettura del file: ${err.message}`);
    }
  };
  reader.onerror = () => {
    parseErrors.value.push('Impossibile leggere il file selezionato.');
  };
  reader.readAsText(file, 'utf-8');
};

const parseCsv = (text) => {
  const out = [];
  const warnings = [];
  const rawLines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (!rawLines.length) return { rows: out, warnings };

  const delimiter = detectDelimiter(rawLines[0]);

  const headerCells = splitCsvLine(rawLines.shift(), delimiter);
  const headerMap = buildHeaderMap(headerCells);

  if (headerMap.email < 0) {
    parseErrors.value.push('Colonna "email" non trovata.');
    return { rows: out, warnings };
  }
  if (headerMap.programsTotal < 0) {
    parseErrors.value.push('Colonna "programmi totali" non trovata.');
    return { rows: out, warnings };
  }
  const hasHistoryColumn = headerMap.programsCompleted >= 0 || headerMap.programsActive >= 0;
  if (!hasHistoryColumn) {
    parseErrors.value.push('Colonna per "programmi da importare" oppure "programmi storici" non trovata.');
    return { rows: out, warnings };
  }

  rawLines.forEach((line, idx) => {
    const cells = splitCsvLine(line, delimiter);
    const email = (cells[headerMap.email] || '').trim();
    if (!email) {
      warnings.push(`Riga ${idx + 2}: email mancante (riga ignorata).`);
      return;
    }

    const totalRaw = (cells[headerMap.programsTotal] || '').trim();
    const completedRaw = headerMap.programsCompleted >= 0 ? (cells[headerMap.programsCompleted] || '').trim() : '';
    const activeRaw = headerMap.programsActive >= 0 ? (cells[headerMap.programsActive] || '').trim() : '';
    const expireRaw = headerMap.expireAt >= 0 ? (cells[headerMap.expireAt] || '').trim() : '';
    const infoOrdine = headerMap.infoOrdine >= 0 ? (cells[headerMap.infoOrdine] || '').trim() : '';
    const orderMeta = parseOrderInfo(infoOrdine);

    const total = parseInt(totalRaw, 10);
    const completedParsed = parseInt(completedRaw, 10);
    const activeParsed = parseInt(activeRaw, 10);
    const completedIsValid = Number.isFinite(completedParsed) && completedParsed >= 0;
    const activeIsValid = Number.isFinite(activeParsed) && activeParsed >= 0;

    if (!Number.isFinite(total) || total <= 0) {
      warnings.push(`Riga ${idx + 2}: "programmi totali" non valido (${totalRaw}). Riga ignorata.`);
      return;
    }
    if (!completedIsValid && !activeIsValid) {
      warnings.push(
        `Riga ${idx + 2}: specificare un numero valido di programmi da importare (attivi) oppure storici. Riga ignorata.`
      );
      return;
    }

    let programsActive = null;
    let programsHistory = null;
    if (activeIsValid) {
      programsActive = activeParsed;
      if (programsActive > total) {
        warnings.push(`Riga ${idx + 2}: "programmi attivi" maggiore di totale. Riga ignorata.`);
        return;
      }
      programsHistory = total - programsActive;
    } else if (completedIsValid) {
      programsHistory = completedParsed;
      if (programsHistory > total) {
        warnings.push(`Riga ${idx + 2}: "programmi storici" maggiore di totale. Riga ignorata.`);
        return;
      }
      programsActive = total - programsHistory;
    }

    if (programsHistory == null || programsActive == null) {
      warnings.push(`Riga ${idx + 2}: impossibile calcolare lo stato dei programmi. Riga ignorata.`);
      return;
    }

    const expireAtStandard = standardizeDate(expireRaw);
    const measurements = extractMeasurements(cells, headerMap);
    const photos = extractPhotos(cells, headerMap);

    out.push({
      _id: `${email}-${idx + 1}`,
      email,
      programTitle: infoOrdine || '',
      orderLabel: orderMeta.label || infoOrdine || '',
      orderRef: orderMeta.orderRef || '',
      productName: orderMeta.productName || '',
      programsTotal: total,
      programsCompleted: programsHistory,
      programsActive,
      expireAt: expireRaw || null,
      expireAtStandard,
      expireAtDisplay: expireAtStandard || (expireRaw || '—'),
      measurements,
      photos
    });
  });

  return { rows: out, warnings };
};

const buildHeaderMap = (headers) => {
  const normalized = headers.map((h) => normalizeHeader(h));
  const findFirst = (...keys) => {
    for (const key of keys) {
      const idx = normalized.indexOf(key);
      if (idx !== -1) return idx;
    }
    return -1;
  };
  const measurementIndexes = {};
  MEASUREMENT_FIELDS.forEach((field) => {
    measurementIndexes[field.key] = findFirst(...(field.headerKeys || []));
  });
  const photoIndexes = {};
  PHOTO_FIELDS.forEach((field) => {
    photoIndexes[field.key] = findFirst(...(field.headerKeys || []));
  });
  return {
    email: findFirst('email'),
    programsTotal: findFirst('programmistotali', 'programmitotali', 'programmistotal', 'programstotal'),
    programsCompleted: findFirst('programmisvolti', 'programmicompletati', 'completed', 'programscompleted'),
    programsActive: findFirst('programmidaimportare', 'programmidisponibili', 'programmipronti', 'active', 'programmidausare'),
    infoOrdine: findFirst('infoordine', 'nomepercorso', 'percorso', 'ordine', 'infoordinee'),
    expireAt: findFirst('datadiscadenza', 'datascadenza', 'scadenza', 'expireat', 'dataexp'),
    measurements: measurementIndexes,
    photos: photoIndexes
  };
};

const normalizeHeader = (raw) =>
  String(raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const detectDelimiter = (line) => {
  const commaCount = (line.match(/,/g) || []).length;
  const semicolonCount = (line.match(/;/g) || []).length;
  if (semicolonCount > commaCount) return ';';
  return ',';
};

const splitCsvLine = (line, delimiter = ',') => {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result.map((value) => value.trim());
};

const standardizeDate = (value) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [d, m, y] = trimmed.split('/');
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  if (/^\d{2}\/\d{2}\/\d{2}$/.test(trimmed)) {
    const [d, m, y] = trimmed.split('/');
    const year = parseInt(y, 10);
    const normalizedYear = year + (year >= 70 ? 1900 : 2000);
    return `${normalizedYear}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return trimmed;
};

const mapRowPayload = (row) => ({
  email: row.email,
  infoOrdine: row.programTitle || null,
  programTitle: row.programTitle || null,
  productName: row.productName || null,
  orderRef: row.orderRef || null,
  orderLabel: row.orderLabel || null,
  programsTotal: row.programsTotal,
  programsCompleted: row.programsCompleted,
  programsActive: row.programsActive,
  expireAt: row.expireAtStandard || row.expireAt || null,
  measurements: row.measurements || {},
  photos: normalizePhotosForPayload(row.photos || {})
});

const importNextChunk = async () => {
  if (!rows.value.length || parseErrors.value.length) return;
  if (!hasPendingRows.value || importingChunk.value) return;
  const end = Math.min(processedCount.value + CHUNK_SIZE, totalRows.value);
  const chunkRows = rows.value.slice(processedCount.value, end);
  if (!chunkRows.length) return;

  importingChunk.value = true;
  try {
    const functions = getFunctions();
    const callable = httpsCallable(functions, 'importProgramHistory');
    const payload = {
      rows: chunkRows.map((row) => mapRowPayload(row))
    };
    const response = await callable(payload);
    const data = response?.data || null;
    if (data) {
      batches.value = [
        ...batches.value,
        {
          batchId: data.batchId,
          summary: data.summary,
          results: data.results,
          reverted: false
        }
      ];
      processedCount.value = end;
    }
  } catch (err) {
    parseErrors.value.push(err.message || 'Errore durante l\'import.');
  } finally {
    importingChunk.value = false;
  }
};

const revertBatch = async (batch) => {
  if (!batch?.batchId) return;
  const state = getRevertState(batch.batchId);
  if (state.loading) return;
  if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
    const confirmed = window.confirm('Eliminare tutti i percorsi creati da questo batch?');
    if (!confirmed) return;
  }
  revertStates.value = {
    ...revertStates.value,
    [batch.batchId]: { loading: true }
  };
  try {
    const functions = getFunctions();
    const callable = httpsCallable(functions, 'deleteImportedPrograms');
    const response = await callable({ batchId: batch.batchId });
    const data = response?.data || {};
    revertStates.value = {
      ...revertStates.value,
      [batch.batchId]: {
        loading: false,
        success: `Eliminati ${data.deletedPercorsi || 0} percorsi e ${data.deletedQuestionari || 0} questionari.`
      }
    };
    batches.value = batches.value.map((entry) =>
      entry.batchId === batch.batchId ? { ...entry, reverted: true } : entry
    );
  } catch (err) {
    revertStates.value = {
      ...revertStates.value,
      [batch.batchId]: {
        loading: false,
        error: err.message || 'Errore durante l\'eliminazione dell\'import.'
      }
    };
  }
};
</script>

<style scoped>
.import-program-history {
  max-width: 920px;
  margin: 0 auto;
  padding: 24px;
  background-color: #ffffff;
}

h1 {
  margin-bottom: 8px;
}

.muted {
  color: #6b7280;
}

.upload-box {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
}

.upload-label {
  padding: 10px 16px;
  border: 1px dashed #94a3b8;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.upload-label input {
  display: none;
}

.btn {
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.2s ease, color 0.2s ease, border 0.2s ease;
}

.btn.primary {
  background: #2563eb;
  color: #fff;
}

.btn.primary:disabled {
  background: #93c5fd;
  cursor: default;
}

.btn.outline {
  background: #fff;
  color: #1f2937;
  border-color: #cbd5f5;
}

.btn.danger {
  background: #dc2626;
  color: #fff;
}

.btn.danger:disabled {
  background: #fca5a5;
  cursor: default;
}

.errors-box {
  border: 1px solid #fecaca;
  background: #fef2f2;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 24px;
}

.errors-box ul {
  margin: 8px 0 0;
  padding-left: 20px;
  color: #b91c1c;
}

.warnings-box {
  border: 1px solid #fde68a;
  background: #fffbeb;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 24px;
}

.warnings-box ul {
  margin: 8px 0 0;
  padding-left: 20px;
  color: #92400e;
}

.preview-card,
.result-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-top: 24px;
  background: #fff;
  box-shadow: 0 12px 28px -16px rgba(15, 23, 42, 0.2);
}

.preview-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.preview-table,
.result-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.preview-table th,
.preview-table td,
.result-table th,
.result-table td {
  border: 1px solid #e5e7eb;
  padding: 8px 10px;
  text-align: left;
}

.summary-list {
  list-style: none;
  padding: 0;
  display: flex;
  gap: 16px;
  margin: 12px 0;
}

.result-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.success-message {
  color: #166534;
  background: #dcfce7;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 8px 0;
}

.error-message {
  color: #b91c1c;
  background: #fee2e2;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 8px 0;
}

.batch-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
}

.batch-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  background: #f8fafc;
}

.batch-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid #cbd5f5;
  font-size: 0.75rem;
  margin-left: 8px;
  text-transform: uppercase;
  color: #1f2937;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.status-chip--success {
  background: #dcfce7;
  color: #166534;
}

.status-chip--warning {
  background: #fef9c3;
  color: #92400e;
}

.status-chip--error {
  background: #fee2e2;
  color: #991b1b;
}
</style>
